import { isLicensed } from '@/utils/licenseStatus';
import { isOrangePiBoard } from '@/utils/orangePi';

/**
 * What to say about a vendo whose hardware has not checked in yet.
 *
 * Ray, 2026-08-05: "this is a trap for first time device registration." A
 * brand-new vendo is shown as FAULTY on two screens at once — the 14-day trial
 * reads as withdrawn, and a NodeMCU that is simply unplugged is told to fetch
 * a computer and a USB cable. Both are the same bug as the Connection tab's
 * red "Not reachable" card: the dashboard reading ABSENCE OF DATA as a BAD
 * STATE.
 *
 * ⛔ WORDING ONLY. Nothing here changes when a trial starts or who gets one.
 */

export type LicenseStatus = 'unactivated' | 'trial' | 'active' | 'expired' | 'lifetime';

export interface LicenseBanner {
   text: string;
   cta: string;
   /** false = keep it visible, but stop shouting. */
   ctaPrimary: boolean;
   /** true = not pressable. Only the waiting-for-power state: activating a
    *  vendo that has never proved itself spends a credit on unproven hardware
    *  and skips a free trial that has not even started (Ray, 2026-09-10). */
   ctaDisabled: boolean;
}

/**
 * The banner across the top of a device card.
 *
 * 🔴 THE HEADLINE BUG. The 14-day trial is one-shot and starts ON THE FIRST
 * HEARTBEAT — "start the one-shot 14-day trial when hardware first proves
 * itself", guarded by `licenseStatus === 'unactivated'`. So until the vendo
 * powers on and checks in it stays `unactivated`, the card said
 * "🔒 License inactive — portal is locked", and an operator concluded the free
 * trial was no longer offered and asked Ray about it.
 *
 * ⚠️ `trialStartedAt` is the discriminator and it must not be widened.
 * Unactivated with NO trialStartedAt = the hardware has never proved itself.
 * Unactivated WITH one = the one-shot trial already ran and ended, and that
 * device really is inactive. Promising "your trial starts by itself" there
 * would promise a second free trial the anti-abuse gate will never grant —
 * that gate is what closed the delete-and-re-register loophole on 2026-06-12.
 */
export const licenseBanner = (d: {
   licenseStatus: LicenseStatus;
   trialStartedAt: string | null;
   /** The board, so an OrangePi (One, PC or Zero 3 — on-board coin reader, no
    *  NodeMCU) is not told to wait for a NodeMCU it does not have. */
   hwBoard?: string | null;
   /** The NodeMCU is reporting right now. */
   nodeMcuOnline?: boolean;
   /** The router is checking in right now. On an OrangePi that IS the
    *  hardware proving itself (coin reader on the board, no NodeMCU). */
   routerOnline?: boolean;
}): LicenseBanner | null => {
   if (isLicensed(d.licenseStatus)) return null;
   if (d.licenseStatus === 'expired')
      return {
         text: '⚠ License expired — portal is locked',
         cta: 'Renew Now',
         ctaPrimary: true,
         ctaDisabled: false,
      };
   // The trial banner is built by the card itself — it needs the day count.
   if (d.licenseStatus === 'trial') return null;

   // The hardware has proved itself yet no trial started: this router already
   // used its free trial. Never "waiting for power" — the next step is a
   // credit, and the button must work (SMART-62, 2026-10-22). An OrangePi
   // proves itself with the router's own heartbeat, so a board that is online
   // with no trial used it before (RB Pisowifi ORP-C2C9A931, prod 2026-10-29:
   // same board trialled in August as ORP-DA5B14E6, card said "waiting").
   const hardwareProved =
      d.nodeMcuOnline || (isOrangePiBoard(d.hwBoard) && d.routerOnline);
   if (!d.trialStartedAt && hardwareProved)
      return {
         text: isOrangePiBoard(d.hwBoard)
            ? '🔒 Free trial already used on this OrangePi — activate with a credit'
            : '🔒 Free trial already used on this router — activate with a credit',
         cta: 'Activate',
         ctaPrimary: true,
         ctaDisabled: false,
      };

   if (!d.trialStartedAt)
      return {
         text: isOrangePiBoard(d.hwBoard)
            ? '⏳ Waiting for your OrangePi to power on. Your free trial starts by itself the moment it comes online.'
            : '⏳ Waiting for your NodeMCU to power on. Your free trial starts by itself the moment it connects to your router.',
         cta: 'Activate',
         // Visible so the operator knows activation exists, but DISABLED — the
         // next action is a power socket, not a purchase, and pressing this
         // would spend a credit on hardware that has never proved itself.
         ctaPrimary: false,
         ctaDisabled: true,
      };

   return {
      text: '🔒 License not activated — portal is locked',
      cta: 'Activate',
      ctaPrimary: true,
      ctaDisabled: false,
   };
};

/**
 * Three states for a NodeMCU, not two.
 *
 * 🔴 A board flashed during registration but never powered on has reported no
 * version, so it was rendered as "too old to update over WiFi — it needs a
 * one-time USB re-flash from a computer". That sends an operator for a laptop
 * and a cable over a board that is not plugged in.
 *
 * ⚠️ Do NOT merge 'needs-power' into 'never-flashed'. Skipping the flash
 * during registration is a supported path and the Hardware tab is where it
 * gets dealt with; the two states have different next actions.
 *
 * ⚠️ And do NOT widen the real USB-re-flash case away: 38 boxes genuinely are
 * below 1.0.0 and must still be told to re-flash. That branch is decided by
 * the version, not here — this only speaks about boxes that have never
 * reported one at all.
 */
export const coinBoxFirstState = (n: {
   lastFlashedAt?: string | null;
   lastHeartbeat?: string | null;
}): 'never-flashed' | 'needs-power' | 'reporting' => {
   // A heartbeat is proof the board is alive, whatever else we do or do not
   // know. Devices registered before lastFlashedAt existed have no flash
   // record, and that is not a reason to tell a working box to re-flash.
   if (n.lastHeartbeat) return 'reporting';
   return n.lastFlashedAt ? 'needs-power' : 'never-flashed';
};

/** The line for each first-heartbeat NodeMCU state. */
export const coinBoxFirstText = (
   state: 'never-flashed' | 'needs-power'
): string =>
   state === 'needs-power'
      ? 'NodeMCU needs power — plug it in and it will check in by itself.'
      : 'Not flashed yet. Flash the NodeMCU from the Hardware tab whenever you have it — the vendo works without one.';
